/**
* Licensed Materials - Property of IBM
* IBM Cognos Products: BI Dashboard
* (C) Copyright IBM Corp. 2017, 2021
* US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
*/

define([
	'underscore',
	'../../lib/@waca/core-client/js/core-client/utils/BrowserUtils',
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../../lib/@waca/core-client/js/core-client/utils/Utils',
	'../../lib/@waca/textwidget/dist/summernote',
	'../../lib/@waca/dashboard-common/dist/utils/HtmlXSSUtils',
	'../widgets/staticwidget/text/ContentEditableSelectionManager',
	'../../lib/@waca/dashboard-common/dist/ui/dialogs/TextToolbarCompactDialog',
	'../../lib/@waca/dashboard-common/dist/ui/AuthoringToolbar',
	'../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI',
	'../../app/nls/StringResources',
	'jquery',
	'./TextFitUtil',
	'../../lib/@waca/textwidget/src/js/base/core/agent',
	'./TextEditorConstants'
],function(_, BrowserUtils, Class, Utils, summernote, HtmlCleanser, ContentEditableSelectionManager, TextToolbar, Toolbar, ContentActionsProviderAPI, resources, $, TextFitUtil, agent, TextEditorConstants) {

	var F12 = 123;
	var ENTER_KEY = 13;

	var WHITELISTED_ELEMENTS = [
		'<div>',
		'<p>',
		'<span>',
		'<b>',
		'<u>',
		'<i>',
		'<br>',
		'<font>',
		'<strong>',
		'<em>',
		'<ul>',
		'<ol>',
		'<li>'
	];

	// Summernote attributes that are used in Properties Pane
	var WHITELISTED_PROPERTIES = [
		'color',
		'font-bold',
		'font-family',
		'font-italic',
		'font-size',
		'font-strikethrough',
		'font-subscript',
		'font-superscript',
		'font-underline',
		'insertOrderedList',
		'insertUnorderedList',
		'text-align',
		'text-format'
	];

	const getFontFamilyList = function(option) {
		const fontFamilyList = TextEditorConstants.FONT_FAMILY_CHOICES;
		if (option && option.omitCssClass) {
			// Remove the cssClass and its subClass as Carbon design no longer displays this property
			fontFamilyList.forEach(font => {
				delete font.cssClass;
				if (font.subCssClasses) {
					delete font.subCssClasses;
				}
			});
		}
		return fontFamilyList;
	};

	var	cleanContentElements = function(content) {
		return HtmlCleanser.cleanseContentElements(content, WHITELISTED_ELEMENTS, true);
	};

	var getTextHTML = function(options) {
		var initialPlaceholder = (options.text ? '<span>'+_.escape(options.text)+'</span>' : '<br>');
		return '<span class="textFitted ' + options.style + 'Color ' + options.style + 'FontSize ' + options.style + 'FontFamily"><p>' + initialPlaceholder + '</p></span>';
	};

	var TextEditor = Class.extend({

		SUMMERNOTE_CLASS: '.summernote',
		EDITING_AREA: '.note-editable',
		TEXT_FITTED: '.textFitted',
		savedSel: null,
		isResponsive: false,
		autoSaveInterval: 2000,
		isBold: false,

		/**
		 * 	Initialize Text editor with the eleemnt where you wanna have text editing capabilities, and a reference to the widget.
		 *
		 * 	@param {Jquery element} options.node The element where we want to initialize summernote
		 * 	@param {Jquery element} options.container Element that contains the node.
		 * 	@param {Jquery element} options.toolbarNode Element that the toolbar will use to place itself
		 * 	@param {Object} options.fonts Object to override the default font properties
		 * 	@param {Object} options.fontSizes Object to override the default font sizes
		 * 	@param {Object} options.widget A reference to the widget where we want text editing capabilities.
		 */

		init: function(options) {
			this.node = options.node;
			this.isResponsive = options.isResponsive;
			// TODO: The initSummernote will update the text content and cleanse it again. Should be consolidated with above code in one place.
			this.initSummernote();
			this.getEditingArea().attr('contenteditable', false);
			this.container = options.container;
			this.widget = options.widget;
			const dashboard = this.widget.getDashboardApi();
			this.colorsService = dashboard.getFeature('Colors');
			const customFonts = dashboard.getFeature('DashboardSettings').get('fonts');

			// FF for 'PropertiesV2'
			const featureChecker = dashboard.getGlassCoreSvc('.FeatureChecker');
			this.isPropertiesV2Enabled = featureChecker && featureChecker.checkValue && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');
			const fontFamilyChoices = getFontFamilyList(this.isPropertiesV2Enabled ? { omitCssClass: true } : null);
			let fonts = Array.isArray(customFonts) ? customFonts.concat(fontFamilyChoices) : fontFamilyChoices;

			this._toolbarDock = dashboard.getFeature('ToolbarDock');
			this._contentActions = dashboard.getFeature('ContentActions');
			this._dashboardState = dashboard.getFeature('DashboardState');
			this._icons = dashboard.getFeature('Icons');

			this.toolbarNode = options.toolbarNode;
			//TODO: options is kept for backward compatibility; it may be removed eventually
			this._fontFamilyChoices = options.fonts || fonts;
			this._fontSizeChoices = options.fontSizes || TextEditorConstants.FONT_SIZE_CHOICES;
			this.shouldResizeViz = options.shouldResizeViz;
			this.supportsLists = options.supportsLists;
			this.numberOfLines = 1;

			this.toolbar = new Toolbar({
				container: $('body'),
				placement: 'top',
				noFocus: true
			});

			let supportsTextWrap = false;
			this._ext = options.extension || {};
			if( this._ext.isTextWrapOn ) {
				supportsTextWrap = true;
			}

			this.textToolbar = new TextToolbar({
				onStateChange: this.onToolbarStateChange.bind(this),
				textNode: this.getEditingArea(),
				properties: this.getToolbarProperties(),
				initialState: options.initialState,
				supportsLists: options.supportsLists,
				supportsTextWrap: supportsTextWrap
			});

			this.fillText();


			const onInit = this._ext.onInit;
			onInit && onInit(this);

			// Save the content so that we can check if the content has changed.
			this.lastSavedData = this.getContent();

			this.toolbar.on('toolbar:hide', this.hideTextToolbar, this);
			this.widget.eventRouter.on('textToolbar:hide', this.hideTextToolbar, this);
			this.widget.eventRouter.on('widget:hideToolbar', this.hideTextToolbar, this);
		},

		/**
		* Initialize summernote in the node given
		*/

		initSummernote: function(){
			var $summerNote = this.node.children(this.SUMMERNOTE_CLASS);
			var preDefinedHtml;
			if (!$summerNote.length) {
				if(this.node.children().length){
					preDefinedHtml = this.node.html();
					this.node.children().remove();
				}
				$summerNote = this.node.append('<div class="summernote"></div>').children('.summernote');
			}
			var isAlreadyInitialized = !!$summerNote.data('summernote');
			if (!isAlreadyInitialized) {
				preDefinedHtml = preDefinedHtml || this.getEditingArea().remove().html();
				this.node.find('.note-editor').remove();
				$summerNote.summernote({
					airMode: true,
					popover: {}, //disable the popover
					disableDragAndDrop: true,
					lineWrapping: true,
					keyMap: this._getSummernoteShortcuts()
				});

				if (preDefinedHtml && HtmlCleanser.isValidHtmlContent(preDefinedHtml)) {
					var $editingArea = this.getEditingArea();
					$editingArea.html(HtmlCleanser.cleanseContentElements(preDefinedHtml, WHITELISTED_ELEMENTS, true));
					// clear old saved jquery textfill font-size value
					$editingArea.find('>p').css('font-size', '');
				}
			}
		},

		/**
		 * gets the default font
		 * @return {Object} font default font properties
		 * @returns {string} font.id id of the font
		 */
		getDefaultFont() {
			let id = '';
			this._fontFamilyChoices.forEach((font) => {
				if (font.default === true) {
					id += font.value;
				}
			});
			return { id: id };
		},

		_getSummernoteShortcuts: function() {
			return {
				pc: {
					'ENTER': 'insertParagraph',
					'CTRL+Z': 'undo',
					'CTRL+Y': 'redo',
					'TAB': '', /* tabbing is handled in onKeyDown */
					'SHIFT+TAB': '',
					'CTRL+B': 'bold',
					'CTRL+I': 'italic',
					'CTRL+U': 'underline',
					'CTRL+SHIFT+L': 'justifyLeft',
					'CTRL+SHIFT+E': 'justifyCenter',
					'CTRL+SHIFT+R': 'justifyRight',
					'CTRL+SHIFT+NUM7': '', /* inserting lists is handled in onKeyDown */
					'CTRL+SHIFT+NUM8': ''
				},
				mac: {
					'ENTER': 'insertParagraph',
					'CMD+Z': 'undo',
					'CMD+SHIFT+Z': 'redo',
					'TAB': '', /* tabbing is handled in onKeyDown */
					'SHIFT+TAB': '',
					'CMD+B': 'bold',
					'CMD+I': 'italic',
					'CMD+U': 'underline',
					'CMD+SHIFT+L': 'justifyLeft',
					'CMD+SHIFT+E': 'justifyCenter',
					'CMD+SHIFT+R': 'justifyRight',
					'CMD+SHIFT+NUM7': '', /* inserting lists is handled in onKeyDown */
					'CMD+SHIFT+NUM8': ''
				}
			};
		},

		destroy: function() {
			if (this.toolbar) {
				this.toolbar.remove();
				this.toolbar = null;
			}
			if (this.textToolbar) {
				this.textToolbar.remove();
				this.textToolbar = null;
			}
			this.selectionManager = null;
			this._getSummerNote().summernote('destroy');
		},

		/**
		 * Initialize ContentEditableSelectionManager in the element matched by the selector.
		 *
		 * @param {String} selector Selector of the element
		 */

		initContentEditable: function(selector){
			this.selectionManager = new ContentEditableSelectionManager(this.node, selector);
		},

		/**
		 * insert a text to the editor
		 * @param {String} text - string text to insert to the editor
		 */
		setText: function(text) {
			this.getEditingArea().html(HtmlCleanser.cleanseContentElements(text, WHITELISTED_ELEMENTS, true));
			this.editingAreaHeight = this._getHeight();

			const onSetText = this._ext.onSetText;
			onSetText && onSetText(this);
		},

		getNode: function(){
			return this.node;
		},

		/**
		 * Defect 230351: Put back focus on text when the focus is stolen by the ODT
		 */
		_putBackFocusOnText: function() {
			this.setFocus();
			this.widget.eventRouter.off('widget:hideToolbar:done');
		},

		setFocus: function() {
			this._getSummerNote().summernote('focus');
		},

		/*
		* Helpers.
		*/

		selectionBindNodeEvents: function() {
			this.fnSelectionOnKeyDown = this.selectionOnKeyDown.bind(this);

			this.node.on('keydown', this.fnSelectionOnKeyDown);
		},

		selectionUnbindNodeEvents: function() {
			if (this.fnSelectionOnKeyDown) {
				this.node.off('keydown', this.fnSelectionOnKeyDown);
				this.fnSelectionOnKeyDown = null;
			}
		},

		selectionOnKeyDown: function(event) {
			this.selectionManager.filterKeypress(event);
		},

		addEditingEvents: function(options) {
			// Detach the enter editing mode gestures.
			this.detachEnterEditEvents();
			this.bindNodeEvents();
			this._startAutoSaver(options);

			//SMEditTest
			const onEditStart = this._ext.onEditStart;
			onEditStart && onEditStart(this);
			//SMEditTest
		},

		removeEditingEvents: function(options) {
			this.unbindNodeEvents();
			this._stopAutoSaver(options);

			// Allow the user to enter editing mode again through gestures.
			this.attachEnterEditEvents();

			const onEditEnd = this._ext.onEditEnd;
			onEditEnd && onEditEnd(this);
		},

		/*
		* Event related
		*/

		onInitialTouchDown: function() {
			this.hasInitiallyTouchedDown = true;
		},

		onInitialTouchUp: function() {
			if (this.hasInitiallyTouchedDown) {
				this.hasInitiallyTouchedDown = false;
				//Hide contextual toolbar
				this.widget.eventRouter.trigger('widget:hideToolbar');
				/**
				 * Even tough toolbar is hidden, it can sometimes be rendered and focused because of the timer set with the SetTimout
				 * Defect 230351 fix: Put back focus on text when the focus is stolen by the ODT
				 */
				this.widget.eventRouter.on('widget:hideToolbar:done', this._putBackFocusOnText, this);
				this.toggleEditing(true);
			} else {
				this.widget.eventRouter.off('widget:hideToolbar:done');
			}
		},

		attachEnterEditEvents: function() {
			if (!this.fnTouchDown && this.widget.isAuthoringMode && this.widget.chromeSelected) {
				this.fnTouchDown = this.onInitialTouchDown.bind(this);
				this.fnTouchUp = this.onInitialTouchUp.bind(this);

				this.node
					.on('touchstart', this.fnTouchDown)
					.on('touchend', this.fnTouchUp)
					.on('mousedown', this.fnTouchDown)
					.on('mouseup', this.fnTouchUp);
			}
		},

		detachEnterEditEvents: function() {
			if (this.fnTouchDown) {
				this.node
					.off('touchstart', this.fnTouchDown)
					.off('touchend', this.fnTouchUp)
					.off('mousedown', this.fnTouchDown)
					.off('mouseup', this.fnTouchUp);

				this.fnTouchDown = null;
				this.fnTouchUp = null;
			}
		},

		save: function(options) {
			let data = this.getContent();
			let lastSavedData = this.lastSavedData;

			if(this.isResponsive) {
				// The auto font-size is screen-size specific, and so does not need to be
				// persisted. We remove it from the string compare to avoid triggering
				// undesired or unnecessary events.
				const _clearFontSize = (html) => {
					const $el = $(`<div>${html}</div>`);
					$el.find(this.TEXT_FITTED).css('font-size', '');
					return $el.html();
				};
				data = _clearFontSize(data);
				lastSavedData = _clearFontSize(lastSavedData);
			}

			//truncate style does not need to be persisted
			data = this._clearTruncateStyle(data);
			lastSavedData = this._clearTruncateStyle(lastSavedData);

			// Only update if the value has changed.
			if (data !== lastSavedData) {
				if (!options) {
					var transactionId = _.uniqueId('_text_');

					this.widget.onPropertyUpdate({
						category: 'saveTitle',
						transactionId: transactionId
					});
				}else{
					options.category = 'saveTitle';
					this.widget.onPropertyUpdate(options);
				}

				this.fillText();
			}
		},

		setTruncateOn: function () {
			this.getEditingArea().find(this.TEXT_FITTED).addClass('truncateTextOn');
			this.getEditingArea().find('p').addClass('truncateText');
		},

		setTruncateOff: function() {
			this.getEditingArea().find(this.TEXT_FITTED).removeClass('truncateTextOn');
			this.getEditingArea().find('p').removeClass('truncateText');
		},

		updateLastSavedText: function(value) {
			if( this.lastSavedData !== value ) {
				this.lastSavedData = value;
			}
		},

		_clearTruncateStyle: function( html) {
			const $el = $(`<div>${html}</div>`);
			$el.find('p').removeClass('truncateText');
			return $el.html();
		},

		updateCssOnParagraphs: function(addedClass,removedClass) {
			this.getEditingArea().find('p').removeClass(removedClass);
			this.getEditingArea().find('p').addClass(addedClass);
		},

		_startAutoSaver: function(options) {
			if (!this.autoSaverInterval) {
				//Save then start autosaver
				this.save(options);
				this.autoSaverInterval = setInterval(this.save.bind(this), this.autoSaveInterval);
			}
		},

		_stopAutoSaver: function(options) {
			if (this.autoSaverInterval) {
				this.save(options);
				clearInterval(this.autoSaverInterval);
				this.autoSaverInterval = null;
			}
		},

		/**
		 * Returns whether the event is for the modifier key (cmd on Mac, ctrl on other platforms).
		 */
		_isModifierKey: function (event) {
			return event.ctrlKey || event.metaKey;
		},

		onKeyDown: function(event) {
			// Prevent arrow keys from moving widget around while editing text.
			event.stopPropagation();
			// TODO backspace will delete the editor, this fix can prevent that, but a better solution needs to be found
			if (this.textIsEmpty() && (event.which === 8 || event.which === 46)) {
				event.preventDefault();
			}

			if(event.which === ENTER_KEY){
				this._resizeViz();
			}

			// handle tabbing and indenting
			if(event.which === 9) {
				let maxNestingLevelReached = false;
				let listItemStartSelected = false;
				const listItems = this.getSelectedListItems();
				const selRange = this._getRefinedSelectionRange();
				listItems.forEach((li) => {
					const $li = $(li);
					if(!maxNestingLevelReached && $li.parentsUntil(this.EDITING_AREA, 'ul, ol').length > 3) {
						maxNestingLevelReached = true;
					}
					if(!listItemStartSelected && this._isNodeStartSelected(li, selRange)) {
						listItemStartSelected = true;
					}
				});
				if(listItems.length && listItemStartSelected) {
					if(event.shiftKey) {
						this._getSummerNote().summernote('outdent');
					} else if(!maxNestingLevelReached) {
						this._getSummerNote().summernote('indent');
					}
				} else {
					if(event.shiftKey) {
						this._focusToolbar();
					} else {
						this._getSummerNote().summernote('tab');
					}
				}
				event.preventDefault();
				this.fillText();
				this.save();
			}

			if(this.supportsLists && (event.which === 55 || event.which === 56) && this._isModifierKey(event) && event.shiftKey) {
				this.modifyList(event.which === 55 ? 'insertUnorderedList' : 'insertOrderedList');
				this.fillText();
				this.save();
				this.updatePropertiesIfNecessary();
			}

			this.selectionOnKeyDown(event);
		},

		_focusToolbar: function() {
			this._toolbarDock.focusODT();
		},

		_isToolbarOpened: function() {
			return this.toolbar && this.toolbar.isOpened;
		},

		hideTextToolbar: function() {
			if(this._isToolbarOpened()){
				this.toolbar.remove();
			}
		},

		show: function(immediateEditing) {
			this.node.show();
			this.fillText();
			if(immediateEditing) {
				this.toggleEditing(true);
			}
		},

		hide: function() {
			this.toggleEditing(false);
			this.node.hide();
		},

		_getNumberOfLines: function () {
			return this.getEditingArea().find('p').length;
		},

		_getHeight: function () {
			return this.getEditingArea().height();
		},

		_resizeVizIfHeightChanged: function(){
			if (this._getHeight() !== this.editingAreaHeight) {
				this._resizeViz();
				this.editingAreaHeight = this._getHeight();
			}
		},

		_resizeViz: function(options = {
			hideToolbar: false
		}){
			if (this.shouldResizeViz){
				this.widget.widgetChromeEventRouter.trigger('title:resizeViz', options);
			}
		},


		/*onKeyUp - Checking for the shift F12 event so that we can exit the textwidget.
		* - Prevent fn-backspace or delete from deleting the widget while editing text.
		*/
		onKeyUp: function(event) {
			if(event.which === 27 && this._isToolbarOpened()){
				this.hideTextToolbar();
				this.node.focus();
			} else if (event.which === 27 || (event.which === F12 && event.shiftKey)) {
				event.stopPropagation();
				this.toggleEditing(false);
				this.container.focus();
			} else if (event.which === 8 || event.which === 46) {
				// delete or fn-backspace
				event.stopPropagation();
			}

			this._resizeVizIfHeightChanged();
			this.numberOfLines = this._getNumberOfLines();
			this.fillText();
			// TODO: The updatePropertiesIfNecessary was added to help offset missing functionality from the
			// TextSelectionNormalizer class. The TextSelectionNormalizer class mimics events (selectionchange) that some browsers
			// don't handle (like older firefox 45). This line can be removed once TextSelectionNormalizer has been
			// restored.
			this.updatePropertiesIfNecessary();
		},

		/**
		 * prevent moving the widget in authoring mode when we highlight text using the mouse
		 */
		onMouseMove: function(event) {
			if (this.hasMouseDownInside) {
				event.stopPropagation();
			}
		},

		onMouseUp: function(event) {
			if (this.hasMouseDownInside) {
				this.hasMouseDownInside = false;

				event.stopPropagation();

				if (this.fnMouseMove) {
					$(document).off('mousemove', this.fnMouseMove);
					this.fnMouseMove = null;
				}
				if (this.fnMouseUp) {
					$(document).off('mouseup', this.fnMouseUp);
					this.fnMouseUp = null;
				}
			}
		},

		onMouseDown: function(event) {
			var target = event.target;
			if (target.tagName !== 'SELECT' && target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA' && !target.isContentEditable) {
				event.preventDefault();
			}
		},

		onBlur: function() {
			this.selection = this.saveSelection();
		},

		onMouseDownInside: function(event) {
			event.stopPropagation();

			this.hasMouseDownInside = true;

			// Add mouse move and up events. Only start listening for them now so that we aren't continuously
			// tracking the mouse movements.
			this.fnMouseMove = this.onMouseMove.bind(this);
			this.fnMouseUp = this.onMouseUp.bind(this);

			$(document).on('mousemove', this.fnMouseMove)
				.on('mouseup', this.fnMouseUp);
		},


		onTouchStart: function(event) {
			// Prevent the move widget handler from taking over.
			event.stopPropagation();
		},

		keepTextSelected: function() {
			if (Utils.isIpad()) {
				this.savedSel = this.saveSelection();
			}
		},

		onTextSelected: function() {
			if(!this.getEditingArea().is(document.activeElement)) {
				// if the selection changes because the user
				// clicked elsewhere, we don't care.
				return Promise.resolve();
			}

			// Instantiate this.selection to prepare for new text selection
			if (this.selection) {
				this.selection = null;
			}

			var currentSelection = this._getSummerNote().summernote('createRange').toString();

			if (!currentSelection || currentSelection !== this.lastSelection) {
				this.showTextToolbar();

				// Throttle the properties page update
				if (this.delayedPropertyUpdate) {
					clearTimeout(this.delayedPropertyUpdate);
				}
				this.delayedPropertyUpdate = setTimeout(function() {
					if(!(Utils.isIpad() && $('SELECT:focus').length > 0)){
						this.delayedPropertyUpdate = null;
						this.updatePropertiesIfNecessary();
					}
				}.bind(this), 500);
				this.lastSelection = currentSelection;

				if(Utils.isIpad()){
					this.savedSel = this.saveSelection();
				}
			}
		},

		bindNodeEvents: function() {
			this.fnOnKeyDown = this.onKeyDown.bind(this);
			this.fnOnKeyUp = this.onKeyUp.bind(this);
			this.fnOnPaste = this.onPaste.bind(this);

			// Need to keep these events on the div since they won't fire on the inner span
			this.node.on('keydown', this.fnOnKeyDown)
				.on('keyup', this.fnOnKeyUp)
				.on('paste', this.fnOnPaste);

			this.fnOnMouseDownInside = this.onMouseDownInside.bind(this);
			this.fnOnBlur = this.onBlur.bind(this);
			this.fnOnTouchStart = this.onTouchStart.bind(this);
			this.fnkeepTextSelected = this.keepTextSelected.bind(this);

			// Special handlers for when we click/touch on the text to prevent some of the widget default behavior (i.e. widget move)
			this.getEditingArea()
				.on('mousedown', this.fnOnMouseDownInside)
				.on('blur', this.fnOnBlur)
				.on('touchstart', this.fnOnTouchStart)
				.on('focusout', this.fnkeepTextSelected);

			this.fnMouseDown = this.onMouseDown.bind(this);
			this.fnSelectionChange = this.onTextSelected.bind(this);

			$(document)
				.on('mousedown', this.fnMouseDown)
				.on('selectionchange', this.fnSelectionChange);
		},

		unbindNodeEvents: function() {
			this.node
				.off('keydown', this.fnOnKeyDown)
				.off('keyup', this.fnOnKeyUp)
				.off('paste', this.fnOnPaste);

			this.getEditingArea()
				.off('mousedown', this.fnOnMouseDownInside)
				.off('touchstart', this.fnOnTouchStart)
				.off('blur', this.fnOnBlur)
				.off('focusout', this.fnkeepTextSelected);

			if (this.fnSelectionChange) {
				$(document).off('selectionchange', this.fnSelectionChange);
				this.fnSelectionChange = null;
			}
			if (this.fnMouseDown) {
				$(document).off('mousedown', this.fnMouseDown);
				this.fnMouseDown = null;
			}
		},

		_replaceContent: function (options) {
			var currentContent = this.node.find(this.EDITING_AREA);
			var prevContent = $(options.value).find(this.EDITING_AREA);
			currentContent.html(prevContent.html());

			this._resizeVizIfHeightChanged();
		},

		_didContentChange: function(value){
			if(value){
				var prevContent = $(value).find(this.EDITING_AREA).html();
				var currentContent = this.getContent();
				return prevContent && currentContent && prevContent !== currentContent;
			}else{
				return false;
			}

		},

		applyContent: function (options) {
			//Reinitialize summernote if necessary after undo/redo
			this.initSummernote();

			if (this._didContentChange(options.value)) {
				this._replaceContent(options);
				if (options.sender === 'TranslationService') {
					this.fillText();
				}
				// We need to update the last saved data here, before replacing content with placeholder. See Defect 254590.
				this.lastSavedData = this.getContent();

				// update placeholder on undo / redo
				if(options.sender === 'UndoRedoController' && this.widget.placeholder) {
					this.widget.updatePlaceholder();
				}
			} else {
				this.lastSavedData = this.getContent();
			}
		},

		onPaste: function(event) {
			this._pasteTextExtractedFromHTML(event);
			this.fillText();
		},

		/** put window behind an accessor to make it possible to unit test. */
		_getWindow: function() {
			return window;
		},

		/** Gets the paste data from the paste event, extracts the text out of whatever else is there,
		* and then inserts it where ever the caret is.
		*/
		_pasteTextExtractedFromHTML: function(event) {
			var pastedData;

			// Stop data actually being pasted into the element. preventDefault() prevents the
			// text from being 'pasted twice', once with the formatting and the second time without it (here).
			event.preventDefault();

			// Get pasted data via clipboard API and insert it where the cursor is.
			// This version is apparently more efficient than the alternative (below).
			if (event.originalEvent.clipboardData) {
				pastedData = event.originalEvent.clipboardData.getData('Text');
				this._getWindow().document.execCommand('insertText', false, pastedData);
			}			else {
				// Special case for IE
				pastedData = this._getWindow().clipboardData.getData('Text');
				var selected = this._getWindow().getSelection().getRangeAt(0);
				selected.deleteContents();
				selected.insertNode(this._getWindow().document.createTextNode(pastedData));
			}
		},

		isEditing: function() {
			return this.container.hasClass('editing');
		},

		onToolbarStateChange: function(changedState, options) {
			let restoreDefaults = options && options.restoreDefaults;
			if (restoreDefaults) {
				// 1. The summernote api for bold, italic and underline only offers toggles.
				// 2. The current text selection may have mixed styles. (e.g: only some letters of a word are bold)
				// ** Therefore, removing all formatting before restoring default styles
				this._getSummerNote().summernote('removeFormat');
			}
			for (let propChanged in changedState) {
				if (propChanged === 'underline' || propChanged === 'bold' || propChanged === 'italic'){
					var value = changedState[propChanged];
					if (!restoreDefaults || value) {
						this.modifyTextFormat(propChanged, options);
					}
				} else if (propChanged === 'fontSize'){
					this.onFontSizeUpdate(propChanged, changedState.fontSize);
				} else if (propChanged === 'font') {
					this.onFontFamilyUpdate('fontFamily', changedState.font);
				} else if (propChanged === 'color') {
					if (this._isHexColor(changedState.color)) {
						this.modifyTextColor(this._findColorIdFromHex(changedState.color));
					} else {
						// For incoming non-hex colors, we directly set the class on the node, and then update properties to let the toolbar pick up the hexcode for the new color
						this.modifyTextColor(changedState.color);
					}
				} else if (propChanged === 'justifyLeft' || propChanged === 'justifyRight' || propChanged === 'justifyCenter') {
					if(changedState[propChanged]) {
						this.modifyTextAlignment(propChanged);
					}
				} else if (propChanged === 'insertUnorderedList' || propChanged === 'insertOrderedList') {
					this.modifyList(propChanged, changedState[propChanged]);
				} else if( propChanged === 'textWrap') {
					this._ext.onTextWrapChanged( changedState[propChanged] );
				}
			}

			this.updatePropertiesIfNecessary();
			if(!options || !options.silent) {
				this.lastActiveStyles = this.getActiveStyles();
				this.fillText();
				this.save();
				this.widget.refreshPropertiesPane();
			}
		},

		_findColorHexFromId: function(colorId){
			const colorPalette = this._getTextColorChoices();
			const found = _.find(colorPalette, (color) => {
				return color.id === colorId;
			});
			if(!found && this.colorsService.isCustomColor(colorId)){
				return this.colorsService.getHexColorFromClassName(colorId);
			}
			return found ? found.hexValue : found;
		},

		_findColorIdFromHex: function (colorValue) {
			const colorPalette = this._getTextColorChoices();
			const found = _.find(colorPalette, (color) => {
				return color.hexValue.toUpperCase() === colorValue.toUpperCase();
			});
			if(!found){
				return this.colorsService.getColorClassName(colorValue);
			}
			return found ? found.id : found;
		},

		convertStyleToToolbarState: function(styles){
			let state = {};
			if (styles['font-size']){
				state.fontSize = styles['font-size'] + '';
			}
			if (styles.color){
				state.color = this._findColorHexFromId(styles.color);
			}
			if (styles['font-family']){
				state.font = this._convertCssToFontId(styles['font-family'])[0];
			}
			if (styles['text-align']){
				state[styles['text-align']] = true;
			}

			state.insertUnorderedList = styles.insertUnorderedList;
			state.insertOrderedList = styles.insertOrderedList;

			//Set the text format applied to true (underline,bold,italic);
			state.bold = state.underline = state.italic = false;
			_.each(styles['text-format'], function (format) {
				state[format] = true;
			});

			return state;
		},

		showTextToolbar: function(){
			if (!this.textToolbar.isRendered) {
				let toolbarState = this.convertStyleToToolbarState(this.getActiveStyles());
				// refresh the properties options before re-render the text toolbar
				this.textToolbar.properties = this.getToolbarProperties();
				this._setTextWrapState(toolbarState);
				this.textToolbar.setState(toolbarState);
				const selectedIds = [ this.widget.getId() ];
				this._toolbarDock.updateODTState([
					{
						name: 'textStyle',
						type: 'SubView',
						responsive: true,
						editable: false,
						changedAction: null,
						subView: this.textToolbar,
						removeThenApplyAction: true,
						section: ContentActionsProviderAPI.ACTION_SECTION.SELECTION_TEXT,
						order: 10,
						action: function() {}
					}
				], selectedIds);
			}
		},

		_setTextWrapState: function(toolbarState) {
			if( this._ext && this._ext.isTextWrapOn ) {
				toolbarState.textWrap = this._ext.isTextWrapOn();
			}
		},

		_selectContent: function () {
			var range = document.createRange();
			var isMissingParagraphElement = this.getEditingArea().find('p').length === 0;
			// FIXME: The paragraph element shouldn't be getting deleted at all, but our current implementation with IE 11 allows this to happen
			var nodeContents = isMissingParagraphElement ? this.getEditingArea().get(0) : this.getEditingArea().find('p').get(0);
			range.selectNodeContents(nodeContents);
			var sel = window.getSelection();
			this._clearSelection(sel);
			sel.addRange(range);
		},

		_focusOnText: function(){
			this._selectContent();
			this.getEditingArea().focus();

			this.showTextToolbar();

			this._selectContent();
			this.getEditingArea().focus(); // Required for Firefox, Defect 202868 fix
			this.widget.refreshPropertiesPane({
				'selectedTab': resources.get('propText')
			});
		},

		toggleEditing: function(editing, options) {
			const wasEditing = this.isEditing(this.container);
			const toggle = editing === undefined ? !wasEditing : editing;

			this.container.toggleClass('editing', toggle);
			this.getEditingArea().attr('contenteditable', toggle);

			if(toggle !== wasEditing) {
				this._resizeViz();
			}

			if (toggle) {
				if(!wasEditing){
					this.addEditingEvents(options);
				}
				this._focusOnText();
			} else if (wasEditing) {
				this._clearSelection(window.getSelection());
				this.removeEditingEvents(options);
				const currentSelection = this.widget.getDashboardApi().getCanvas().getSelectedContentList();
				const selectedIds = currentSelection.map(content => content.getId());
				this._toolbarDock.updateODTState(this._contentActions.getContentActionList(selectedIds), selectedIds);
				//when toggle is off, remove the text details tab from the property panel by refreshing the property panel
				this.widget.refreshPropertiesPane();
			}
		},

		_removeClassFromSelection: function (command) {
			var map = {
				'fontSize': /rt_fontSize(\d+|\w+)/g,
				'fontFamily': /rt_fontFamily\w+(-\w+)?/g,
				'textColor': /customColor.+|color\d+|grayShade\d+|responsiveColor/g
			};
			if (command && map[command]) {
				return function (index, cls) {
					return (cls.match(map[command]) || []).join(' ');
				};
			}
		},

		_addClassToSelection: function (command, value) {
			var map = {
				'fontSize': 'rt_fontSize',
				'fontFamily': '',
				'textColor': ''
			};

			return  map[command] + value;
		},


		getToolbarProperties: function(){
			var colorPalette = this._getTextColorChoices();
			var colorValues = [];
			_.each(colorPalette, (color)=> {
				colorValues.push(color.hexValue);
			});
			return {
				'fonts': this._fontFamilyChoices,
				'fontSizes': this._fontSizeChoices,
				'colors': colorValues
			};
		},

		/**
		 * livewidget_cleanup: [todo]  we might need to move this to a proper place..
		 * Implementation of {@link PropertiesProviderAPI#getPropertyLayoutList}
		 */
		getPropertyLayoutList: function() {
			if (!this.isEditing()) {
				return [];
			}
			let layoutList;
			if (this.isPropertiesV2Enabled) {
				layoutList =  [{
					id: 'textDetail',
					type: 'Section',
					label: resources.get('sectionName_details'),
					position: 1
				}, {
					id: 'fontFamilyLine',
					type: 'SingleLineLinks',
					position: 1,
					items: [{
						id: 'fontFamily',
						align: 'left',
						items: []
					}, {
						id: 'fontSize',
						align: 'middle',
						items: []
					}, {
						id: 'textColor',
						align: 'right',
						items: []
					}
					]
				}, {
					id: 'styleLine',
					type: 'SingleLineLinks',
					position: 2,
					items: [{
						id: 'style',
						align: 'left',
						items: []
					}, {
						id: 'alignment',
						align: 'right',
						items: []
					}
					]
				},...this.getPropertyLayoutControlList()];
			} else {
				layoutList =  [{
					id: 'textDetail',
					type: 'Group',
					label: resources.get('tabName_textDetails'),
					position: 1
				}, {
					id: 'fontFamily',
					type: 'Section',
					collapsible: false,
					label: resources.get('textFontFamily'),
					position: 1
				}, {
					id: 'fontFamilyLine',
					type: 'SingleLineLinks',
					items: [{
						id: 'left',
						align: 'left',
						items: []
					}, {
						id: 'right',
						align: 'right',
						items: []
					}]
				}, ...this.getPropertyLayoutControlList()];
			}



			return layoutList;
		},

		getPropertyList: function() {
			return [];
		},

		getPropertyLayoutControlList: function() {
			if (!this.isEditing()) {
				return [];
			}

			let layoutControlList = [];
			this.lastActiveStyles = this.getActiveStyles();

			layoutControlList.push(...this._getFontFamilyProperties());
			layoutControlList.push(this._getFontSizeProperty());
			layoutControlList.push(this._getTextColorProperty());
			layoutControlList.push(this._getFontWeightProperty());
			layoutControlList.push(this._getTextAlignmentProperty());
			if(this.supportsLists) {
				layoutControlList.push(this._getListProperty());
			}

			return layoutControlList;
		},

		_getFontFamilyProperties: function() {
			const activeStyle = this.lastActiveStyles['font-family'];
			let selected = activeStyle ? this._convertCssToFontId(activeStyle) : [];
			let cssClass = this._findFontFamilyItemFromValue(selected[0], this._fontFamilyChoices);
			if (!cssClass) {
				selected = [ this.getDefaultFont().id ];
				cssClass = this._findFontFamilyItemFromValue(selected[0], this._fontFamilyChoices);
			}
			const subCssClass = _.find(cssClass.subCssClasses, (subCssClass) => {
				// if subCssClass is given from select, find subCss with given selected value, otherwise find it from cssClass
				return selected.length > 1 ? subCssClass.value === selected[1] : subCssClass.cssClass === cssClass.cssClass;
			});
			const fontFamilyProperty = [];
			if (this.isPropertiesV2Enabled) {
				fontFamilyProperty.push({
					id: 'fontFamily',
					editor: {
						sectionId: 'textDetail.fontFamilyLine.fontFamily',
						position: 1,
						uiControl: {
							type: 'DropDown',
							ariaDescribedby: resources.get('textFontFamily'),
							options: this._fontFamilyChoices,
							responsive: false,
							style: 'width: 152px;',
							value: selected[0],
							onChange: this.onFontFamilyUpdate.bind(this)
						}
					}
				});
			} else {
				fontFamilyProperty.push({
					id: 'fontFamily',
					editor: {
						sectionId: 'textDetail.fontFamily.fontFamilyLine.left',
						position: 1,
						uiControl: {
							type: 'DropDown',
							ariaDescribedby: resources.get('textFontFamily'),
							options: this._fontFamilyChoices,
							responsive: false,
							style: 'width: 156px;',
							value: selected[0],
							onChange: this.onFontFamilyUpdate.bind(this)
						}
					}
				}, {
					id: 'fontFamilySubClass',
					editor: {
						sectionId: 'textDetail.fontFamily.fontFamilyLine.left',
						position: 2,
						hidden: !cssClass || !cssClass.subCssClasses || cssClass.subCssClasses.length === 0,
						uiControl: {
							type: 'DropDown',
							ariaDescribedby: resources.get('textFontFamily'),
							options: cssClass && cssClass.subCssClasses,
							value: subCssClass && subCssClass.value,
							onChange: this.onFontFamilyUpdate.bind(this),
							style: 'width:80px;',
							responsive: false
						}
					}
				});
			}
			return fontFamilyProperty;
		},

		_getFontSizeProperty: function() {
			let sectionId;
			if (this.isPropertiesV2Enabled) {
				sectionId = 'textDetail.fontFamilyLine.fontSize';
			} else {
				sectionId = 'textDetail';
			}
			const property =  {
				id: 'fontSize',
				editor: {
					sectionId: sectionId,
					position: 2,
					uiControl: {
						type: 'DropDown',
						label: resources.get('textFontSize'),
						ariaDescribedby: resources.get('textFontSize'),
						options: this._fontSizeChoices,
						onChange: this.onFontSizeUpdate.bind(this),
						value: this.isResponsive ? 'auto' : this.lastActiveStyles['font-size'] + ''
					}
				}
			};
			if (this.isPropertiesV2Enabled) {
				property.editor.uiControl.style = 'width:88px;';
			}
			return property;
		},

		_getTextColorProperty: function () {
			const paletteItems = this._getTextColorChoices();
			let selected = _.find(paletteItems, palette => palette.id === this.lastActiveStyles.color) || {
				name: this.lastActiveStyles.color,
				value: ''
			};
			let sectionId;
			if (this.isPropertiesV2Enabled) {
				sectionId = 'textDetail.fontFamilyLine.textColor';
			} else {
				sectionId = 'textDetail';
			}
			return {
				id: 'textColor',
				editor: {
					sectionId: sectionId,
					position: 3,
					uiControl: {
						paletteType: 'DashboardColorSet',
						type: 'ColorPicker',
						showHexValue: true,
						addButton: true,
						label: resources.get('propTextColor'),
						open: false,
						ariaLabel: resources.get('propTextColor'),
						items: paletteItems,
						allowSameSelection: true,
						selectedName: selected ? selected.name : '',
						value: selected ? selected.value : '',
						onChange: this.onTextColorUpdate.bind(this)
					}
				}
			};
		},

		getCustomColorList: function() {
			let colorList = [];
			let classNames = [];
			const allSpans = this.getEditingArea().find('span');
			allSpans.each((index, span) => { classNames.push(span.className); });
			const allClassNames = classNames.join(' ');
			const customColors = this.colorsService.getCustomColorSet();
			customColors.forEach((customColor) => {
				if(allClassNames.indexOf(customColor.id) !== -1){
					colorList.push(customColor.hexValue);
				}
			});
			return colorList;
		},

		_getFontWeightProperty: function () {
			let sectionId;
			if (this.isPropertiesV2Enabled) {
				sectionId = 'textDetail.styleLine.style';
			} else {
				sectionId = 'textDetail';
			}
			return {
				id: 'textFormat',
				editor: {
					sectionId: sectionId,
					position: 4,
					uiControl: {
						type: 'IconPicker',
						label: resources.get('textStyle'),
						useToggling: true,
						allowMultiple: true,
						items: [{
							name: 'bold',
							label: resources.get('propToolbarPickBold'),
							value: 'common-text-bold'
						}, {
							name: 'italic',
							label: resources.get('propToolbarPickItalic'),
							value: 'common-text-italic'
						}, {
							name: 'underline',
							label: resources.get('propToolbarPickUnderline'),
							value: 'common-text-underline'
						}],
						values: this.lastActiveStyles['text-format'],
						onChange: this.onTextFormatUpdate.bind(this)
					}
				}
			};
		},

		_getTextAlignmentProperty: function () {
			let sectionId;
			if (this.isPropertiesV2Enabled) {
				sectionId = 'textDetail.styleLine.alignment';
			} else {
				sectionId = 'textDetail';
			}
			return {
				id: 'alignmentPicker',
				editor: {
					sectionId: sectionId,
					position: 5,
					uiControl: {
						type: 'IconPicker',
						label: resources.get('propAlignPicker'),
						items: [{
							name: 'justifyLeft',
							label: resources.get('propAlignPickLeft'),
							value: 'common-left-align'
						}, {
							name: 'justifyCenter',
							label: resources.get('propAlignPickCenter'),
							value: 'common-center-align'
						}, {
							name: 'justifyRight',
							label: resources.get('propAlignPickRight'),
							value: 'common-right-align'
						}],
						values: this.lastActiveStyles['text-align'],
						onChange: this.onTextAlignmentUpdate.bind(this)
					}
				}
			};
		},


		_getListProperty: function () {
			let selected;
			if(this.lastActiveStyles['insertUnorderedList']) {
				selected = 'insertUnorderedList';
			} else if (this.lastActiveStyles['insertOrderedList']) {
				selected = 'insertOrderedList';
			}
			let position;
			if (this.isPropertiesV2Enabled) {
				position = 3;
			} else {
				position = 6;
			}
			return {
				id: 'listPicker',
				editor: {
					sectionId: 'textDetail',
					position: position,
					uiControl: {
						type: 'IconPicker',
						label: resources.get('propList'),
						useToggling: true,
						items: [{
							name: 'insertUnorderedList',
							label: resources.get('propUnorderedList'),
							value: this._icons.getIcon('bullet-list').id
						}, {
							name: 'insertOrderedList',
							label: resources.get('propOrderedList'),
							value: this._icons.getIcon('number-list').id
						}],
						values: selected,
						onChange: this.onListUpdate.bind(this)
					}
				}
			};
		},

		updateTextToolbar: function(activeStyles) {
			const styles = activeStyles ? activeStyles : this.getActiveStyles();
			const states = this.convertStyleToToolbarState(styles);
			// refresh the properties options before re-render the text toolbar
			this.textToolbar.properties = this.getToolbarProperties();
			this._setTextWrapState(states);
			this.textToolbar.setState(states);
		},

		updatePropertiesIfNecessary: function () {
			if (this.isEditing()) {
				var activeStyles = this.getActiveStyles();
				var filteredActiveStyles = _.pick(activeStyles, WHITELISTED_PROPERTIES);
				var filteredLastActiveStyles = _.pick(this.lastActiveStyles || {}, WHITELISTED_PROPERTIES);
				if (!_.isEqual(filteredActiveStyles, filteredLastActiveStyles)) {
					this.lastActiveStyles = activeStyles;
					this.updateTextToolbar(activeStyles);
					this.widget.refreshPropertiesPane();
				}
			}
		},

		_convertCssToFontId: function(fontCss){
			var fontId;
			fontId = fontCss.replace(/["\s]+/g, '');
			fontId = fontId.toLowerCase().split(',');
			fontId = fontId.length ? fontId[0].split('-') : null;
			return fontId;
		},

		getFontFamilySubClassSpec: function (cssItem, defaultValue) {
			if (!cssItem) {
				return [];
			}
			if (!defaultValue && cssItem.subCssClasses) {
				for (var i = 0; i < cssItem.subCssClasses.length; i++) {
					if (cssItem.subCssClasses[i].cssClass === cssItem.cssClass) {
						defaultValue = cssItem.subCssClasses[i].value;
						break;
					}
				}
			}

			var property = {
				'type': 'DropDown',
				'name': 'fontFamilySubClass',
				'id': 'fontFamilyGroup',
				'ariaDescribedby': resources.get('textFontFamily'),
				'defaultValue': defaultValue,
				'options': cssItem.subCssClasses,
				'onChange': this.onFontFamilyUpdate.bind(this),
				'style': 'width:80px;',
				'responsive': false
			};

			if (!cssItem.subCssClasses || cssItem.subCssClasses.length === 0) {
				property.hidden = true;
			}

			return property;
		},

		modifyTextFormat: function (value) {
			if (!this.isEditing()) {
				return;
			}

			if (agent.isFF && (value == 'bold' || value == 'underline')) {
				// [201576] Patch for bold + underline props not showing up in FF
				this.patchUnderline(value);
			} else if (value) {
				this._getSummerNote().summernote(value);
			}
			this.fillText();
		},

		onListUpdate: function(propertyName, propertyValue) {
			if (propertyValue && propertyValue[0] && propertyValue[0].name) {
				this.modifyList(propertyValue[0].name);
			} else {
				const activeList = this.getActiveStyles()['insertUnorderedList'] ? 'insertUnorderedList' : 'insertOrderedList';
				this.modifyList(activeList);
			}
			this.fillText();
			this.save();
			this.updateTextToolbar();
		},

		modifyList: function (propertyName, propertyValue) {
			if (!this.isEditing() || this.getActiveStyles()[propertyName] ===  propertyValue ) {
				return;
			}
			this._getSummerNote().summernote(propertyName);
			this._styleListItems();
		},

		/**
		 * Patch for firefox, applying underline after bold would break
		 * Works fine when we have <b><u>, fails spectacularly when we have <u><b>, i.e. underline then bold works, bold then underline fails.
		 * @param value {String} bold or underline
		 */
		patchUnderline: function(value) {
			if (value == 'bold') {
				this.isBold = !this.isBold;
				this._getSummerNote().summernote(value);
			} else if (value == 'underline') {
				if (this.isBold) {
					// remove bold and reapply after underline so we always have <b><u>, never <u><b> since that breaks in FF
					this._getSummerNote().summernote('bold');
					this._getSummerNote().summernote('underline');
					this._getSummerNote().summernote('bold');
				} else {
					this._getSummerNote().summernote('underline');
				}
			}
		},

		/*
		 * Modify the font family: Anton, Roboto, etc.
		 */
		modifyFontFamily: function (cssClass) {
			if (!this.isEditing()) {
				return;
			}

			let removedClass = this._removeClassFromSelection('fontFamily');
			let addedClass = this._addClassToSelection('fontFamily', cssClass);
			this._getSummerNote().summernote('updateClassToSelection', {
				remove: removedClass,
				add: addedClass
			});

			this._styleListItems();

			this.getEditingArea().focus();

			this.fillText();
		},

		_findFontFamilyItemFromValue: function (value, items) {
			let current;
			for (var i = 0; i < items.length; i++) {
				if (items[i].value === value) {
					return items[i];
				} else if (this.lastActiveStyles && this.lastActiveStyles['font-family'] && items[i].value === this._convertCssToFontId(this.lastActiveStyles['font-family'])[0]) {
					current = items[i];
				}
			}
			if (current) {
				let subCssClassMatch = this._findFontFamilyItemFromValue(value, current.subCssClasses || []);
				if (subCssClassMatch) {
					return subCssClassMatch;
				}
			}

			return null;
		},

		_cleanInlineStyle: function () {
			this.getEditingArea().find('span, li').css('font-size', '');
		},

		/*
		 * Modify the font size.
		 */
		modifyFontSize: function (value) {
			if (!this.isEditing()) {
				return;
			}

			this.isResponsive = value === 'auto';

			let removeClass = this._removeClassFromSelection('fontSize');
			if (!this.isResponsive) {
				this.unFillText();
				this._getSummerNote().summernote('fontSize', parseInt(value));
				this._styleListItems();
			} else {
				this._cleanInlineStyle();
			}

			this._getSummerNote().summernote('updateClassToSelection', {
				remove: removeClass
			});

			this.fillText();
			this.getEditingArea().focus();
			this._resizeViz();
		},

		_isHexColor: function(val) {
			return val.indexOf('#') === 0 && val.length === 7;
		},

		/*
		 * Modify the text color.
		 */
		modifyTextColor: function (value) {
			if (!this.isEditing()) {
				return;
			}
			let addedClass;
			let removedClass = this._removeClassFromSelection('textColor');
			// Check for the transparent color.
			if (value !== 'transparent') {
				addedClass = this._addClassToSelection('textColor', value);
			}

			this._getSummerNote().summernote('updateClassToSelection', {
				remove: removedClass,
				add: addedClass
			});

			this._styleListItems();

			this.getEditingArea().focus();
		},


		onTextFormatUpdate: function (propertyName, propertyPayload) {
			var formatStyles = ['bold', 'underline', 'italic'];
			var selected = this.getActiveStyles()['text-format'];

			formatStyles.forEach(function (format) {
				var styleApplied = [];
				if (propertyPayload) {
					styleApplied = $.grep(propertyPayload, function (property) {
						return property.name === format;
					});
				}
				if ((styleApplied.length > 0 && selected.indexOf(format) === -1) || (styleApplied.length === 0 && selected.indexOf(format) > -1)) {
					this.modifyTextFormat(format);
				}
			}.bind(this));

			this.fillText();
			this.save();
			this.updateTextToolbar();
		},

		onTextAlignmentUpdate: function (propertyName, propertyPayload) {
			if (propertyPayload && propertyPayload[0] && propertyPayload[0].name) {
				this.modifyTextAlignment(propertyPayload[0].name);
			}
			this.fillText();
			this.save();
			this.updateTextToolbar();
		},

		modifyTextAlignment: function(value){
			if (!this.isEditing()) {
				return;
			}

			let alignment = value.replace('justify', '');
			let selectedTextNodes = this.getSelectedParagraphs();
			for (let node of selectedTextNodes) {
				node.style.textAlign = alignment;
			}
			//Changing the text alignment loses focus on the text
			this.getEditingArea().focus();
		},

		onFontSizeUpdate: function (propertyName, value) {
			this.modifyFontSize(value);
			this.fillText();
			if (Utils.isIpad()) {
				this.restoreSelection();
			}
			this.getEditingArea().focus();
			this.updateTextToolbar();
		},

		onFontFamilyUpdate: function (propertyName, value) {
			if (value) {
				var styleItem = this._findFontFamilyItemFromValue(value, this._fontFamilyChoices);
				if (!styleItem) {
					return;
				}

				this.modifyFontFamily(styleItem.cssClass);

				if (propertyName === 'fontFamily') {
					this.widget.refreshPropertiesPane();
				}

				//Force auto-resize of text because some fonts are different default sizes
				this.fillTextAfterDelay(500);
				if (Utils.isIpad()) {
					this.restoreSelection();
				}
				this.getEditingArea().focus();
				this.updateTextToolbar();
			}
		},

		onTextColorUpdate: function (propertyName, propertyValueInfo) {
			if (this.selection) {
				this.restoreSelection(this.selection);
				this.selection = null;
			}
			this.modifyTextColor(propertyValueInfo.name);

			this.fillText();
			this.updateTextToolbar();
		},

		textIsEmpty: function() {
			return (this._getSummerNote().summernote('isEmpty') ||
			/*Firefox*/this.getEditingArea().get(0).textContent.length === 0 && this.numberOfLines == 1);
		},

		getEditingArea: function(){
			return this.node.find(this.EDITING_AREA);
		},

		_getSummerNote: function(){
			this.initSummernote();
			return this.node.children(this.SUMMERNOTE_CLASS);
		},

		getContent: function() {
			return 	this.getEditingArea().html();
		},

		getTitleFromHtml: function(html) {
			var titleHtml = html;
			var title = '';
			$(titleHtml).find('p, li').each(function(index, el) {
				title = title + ' ' + el.textContent;
			});
			return title.trim();
		},

		fillText: function() {
			if (!this.node.is(':visible')) {
				return;
			}
			if(this.isResponsive) {
				this.node.addClass('textFillNoScroll');
				this.node.find(this.TEXT_FITTED).css('word-wrap', 'normal');

				let savedLineAlignment = [];
				if(BrowserUtils.isIE()) {
					// Texfit fails in IE and Edge if the text is aligned right (related MS issue 30900154)
					// Scrollwidth erroneously 1px smaller than actual fools the size acceptance test
					this.getEditingArea().find('p, li').each(function(index, el) {
						savedLineAlignment.push(el.style.textAlign);
						el.style.textAlign = 'left';
					});
				}

				const options = {
					maxFontSize: Math.ceil(this.node.height()),
					minFontSize: 1,
					multiLine: true,
					detectMultiLine: false
				};

				TextFitUtil.fillText(this.getEditingArea()[0], options);

				if(savedLineAlignment.length) {
					this.getEditingArea().find('p, li').each(function(index, el) {
						el.style.textAlign = savedLineAlignment[index];
					});
				}
			}
		},

		fillTextAfterDelay: function(delay) {
			setTimeout(this.fillText.bind(this), delay);
		},

		unFillText: function() {
			this.node.find(this.TEXT_FITTED).css({
				'font-size': '',
				'word-wrap': ''
			});
			this.node.removeClass('textFillNoScroll');
		},

		/**
		 * Styling related function
		 */
		getActiveStyles: function() {
			var styles = this._getSummerNote().summernote('currentStyle');

			this._getActiveTextFormat(styles);
			this._getActiveTextAlignment(styles);
			this._getActiveTextColor(styles);
			this._getActiveFontSize(styles);
			this._getActiveLists(styles);

			return styles;
		},

		_getActiveLists: function(context) {
			context.insertUnorderedList = context['list-style'] === 'unordered';
			context.insertOrderedList = context['list-style'] === 'ordered';
		},

		_getActiveTextFormat: function(context) {
			var obj = context['text-format'];
			if (!obj) {
				context['text-format'] = obj = [];
			}

			if (context['font-bold'] === 'bold') {
				obj.push('bold');
			}
			if (context['font-underline'] === 'underline') {
				obj.push('underline');
			}
			if (context['font-italic'] === 'italic') {
				obj.push('italic');
			}
		},

		_getActiveTextAlignment: function(context) {
			let selectedTextNodes = this.getSelectedParagraphs();

			// If multiple lines have different text alignments
			// will not highlight an alignment in the property panel
			if (selectedTextNodes && selectedTextNodes.length > 0) {
				let style = selectedTextNodes[0].style.textAlign;
				for(let node of selectedTextNodes) {
					if(node.style.textAlign !== style) {
						context['text-align'] = '';
						return;
					}
				}

				switch (style) {
					case 'center':
						context['text-align'] = 'justifyCenter';
						break;
					case 'right':
						context['text-align'] = 'justifyRight';
						break;
					default:
						context['text-align'] = 'justifyLeft';
				}
			}
		},

		_getActiveTextColor: function(context) {
			if (!context.color) {
				return;
			}

			var match = this._getActiveTextColor_Checker(context.color);

			if (match) {
				context.color = match;
			}
		},

		_getActiveTextColor_Checker: function(rgbColor) {
			let match = null;
			if (rgbColor) {
				const colors = this._getTextColorChoices();
				const hex = this._rgbToHex(rgbColor);
				for (let color of colors) {
					// Find key corresponding to the node's color value.
					if (hex && hex === color.hexValue) {
						match = color.id;
						break;
					}
				}
				//If the color is not found then it is a customColor
				if (!match && hex) {
					match = this.colorsService.getColorClassName(hex);
				}
			}
			return match;
		},

		_getActiveFontSize: function(context) {
			if (!context['font-size']) {
				return;
			}
			if (this.isResponsive) {
				context['font-size'] = 'auto';
			}
		},

		_rgbToHex: function(rgb) {
			rgb = rgb.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);
			return (rgb && rgb.length === 4) ? '#' +
			('0' + parseInt(rgb[1], 10).toString(16)).slice(-2) +
			('0' + parseInt(rgb[2], 10).toString(16)).slice(-2) +
			('0' + parseInt(rgb[3], 10).toString(16)).slice(-2) : '';
		},

		_getTextColorChoices: function () {
			return this.colorsService.getDashboardColorSet().filter((item)=> { return item.id !== 'transparent';});
		},

		_copyTextStyles: function($from, $to) {
			const hexColor = this._rgbToHex($from.css('color'));
			const fontId = this._convertCssToFontId($from.css('font-family'))[0];
			$to.removeClass();
			const colorClass = this._findColorIdFromHex(hexColor);
			if(colorClass) {
				$to.addClass(colorClass);
			}
			const fontFamilyItem = this._findFontFamilyItemFromValue(fontId, this._fontFamilyChoices);
			if(fontFamilyItem && fontFamilyItem.cssClass) {
				$to.addClass(fontFamilyItem.cssClass);
			}
			if(!this.isResponsive) {
				$to.css({
					'font-size': $from.css('font-size')
				});
			}
		},

		/**
		 * Returns a refined clone of the current selection range.
		 * Useful for Range.compareBoundaryPoints calls.
		 */
		_getRefinedSelectionRange: function() {
			let selRange = window.getSelection().getRangeAt(0).cloneRange();
			let selStartEl = selRange.startContainer;
			let selStartOffset = selStartEl.firstChild ? 0 : selRange.startOffset;
			while(selStartEl.firstChild) {selStartEl = selStartEl.firstChild;}
			// Summernote has a patch that inserts bogus chars when styling with no selection
			// Range.compareBoundaryPoints will produce unexpected results if there is more than one of them on a boundary.
			// Logged https://github.com/summernote/summernote/issues/3035, this is a workaround for now.
			const ZERO_WIDTH_SPACE = '\uFEFF';
			if(selStartOffset > 0 && selStartEl.textContent[selStartOffset - 1] === ZERO_WIDTH_SPACE) {
				selStartOffset -= 1;
				if(selStartOffset === 0) {
					const textNodeWalker = document.createTreeWalker($(selStartEl).closest('p, li')[0], window.NodeFilter.SHOW_TEXT);
					while(selStartEl !== textNodeWalker.nextNode());
					let previousTextNode = textNodeWalker.previousNode();
					while(previousTextNode && previousTextNode.textContent[previousTextNode.textContent.length - 1] === ZERO_WIDTH_SPACE) {
						previousTextNode.textContent = previousTextNode.textContent.slice(0, -1);
						if(previousTextNode.parentNode.innerText === '') {
							const toRemove = previousTextNode.parentNode;
							previousTextNode = textNodeWalker.previousNode();
							toRemove.remove();
						}
					}
				}
			}
			selRange.setStart(selStartEl, selStartOffset);
			return selRange;
		},

		_isNodeStartSelected: function(node, selRange) {
			while(node.firstChild) {node = node.firstChild;}
			let paragraphRange = document.createRange();
			paragraphRange.setStart(node, 0);
			return selRange.compareBoundaryPoints(window.Range.START_TO_START, paragraphRange) !== 1;
		},

		_styleListItems: function() {
			const listItems = this.getSelectedListItems();
			if(listItems.length) {

				const selRange = this._getRefinedSelectionRange();
				listItems.forEach((node) => {
					const $node = $(node);
					if(this._isNodeStartSelected(node, selRange)) {
						// The li will be styled; Protect li children against li styles before applying them.
						$node.contents().each((index, childNode) => {
							const $childNode = $(childNode);
							if(childNode.nodeType === window.Node.TEXT_NODE) {
								const $span = $('<span/>');
								this._copyTextStyles($node, $span);
								$childNode.wrap($span);
							} else {
								this._copyTextStyles($childNode, $childNode);
							}
						});
						while(node.firstChild) {node = node.firstChild;}
						this._copyTextStyles($(node.parentNode), $node);
					}
				});
			}
		},

		getSelectedListItems: function() {
			return this.getSelectedParagraphs().filter(node => node.nodeName === 'LI');
		},

		getSelectedParagraphs: function() {
			let selection = window.getSelection();
			if (!selection.rangeCount) {
				return [];
			}

			let range = selection.getRangeAt(0);

			if(range.startContainer === this.getEditingArea()[0]) {
				// Happens on IE if selection was made with ctrl + a (select all)
				return this.getEditingArea().find('p, li').toArray();
			}

			let start = $(range.startContainer).closest('p, li').get(0);
			let end = $(range.endContainer).closest('p, li').get(0);

			if (start === end) {
				return start ? [start] : [];
			}

			let nodes = [];
			let inRange = false;
			$(range.commonAncestorContainer).find('p, li').each(function() {
				if (this === start) {
					inRange = true;
				}
				if (inRange) {
					nodes.push(this);
				}
				if (this === end) {
					inRange = false;
				}
			});

			return nodes;
		},

		saveSelection: function() {
			if (window.getSelection) {
				var sel = window.getSelection();
				if (sel.getRangeAt && sel.rangeCount) {
					return sel.getRangeAt(0);
				}
			} else if (document.selection && document.selection.createRange) {
				return document.selection.createRange();
			}
			return null;
		},

		// IE11 throws error 800a025e if certain html elements are present in the
		// selection during the call to removeAllRanges
		_clearSelection: function(selection) {
			if (document.body.createTextRange) {
				// We are in IE. Use the workaround.
				const range = document.body.createTextRange();
				range.collapse();
				range.select();
			} else {
				// No createTextRange, we are in a modern browser.
				selection.removeAllRanges();
			}
		},

		restoreSelection: function(range) {
			if (range) {
				if (window.getSelection) {
					var sel = window.getSelection();
					this._clearSelection(sel);
					sel.addRange(range);
				}	else if (document.selection && range.select) {
					range.select();
				}
			}
		},

		hasContent: function() {
			const $editingArea = this.getEditingArea();
			return !!$editingArea.text().length || !!$editingArea.find('li').length;
		},

		hasText: function() {
			return !!this.getEditingArea().text().length;
		}
	});

	TextEditor.cleanContentElements = cleanContentElements;
	TextEditor.getTextHTML = getTextHTML;
	TextEditor.FONTSIZE_CHOICES = TextEditorConstants.FONT_SIZE_CHOICES;

	return TextEditor;
});
